import { useState, useEffect } from 'react'
import { Check, X, Clock, Sparkles, Calendar, User as UserIcon, MapPin, MessageSquare, Filter, Inbox, FileText, AlertCircle } from 'lucide-react'
import { leaveAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'

const STATUS_STYLES: Record<string, string> = {
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 ring-red-200',
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
}

const STATUS_DOTS: Record<string, string> = {
  APPROVED: 'bg-emerald-500',
  REJECTED: 'bg-red-500',
  PENDING: 'bg-amber-500',
}

const STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PENDING: 'Pending',
}

export default function AdminLeavePage() {
  const { success, error: toastError } = useToast()
  const [requests, setRequests] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('PENDING')
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  const [actingId, setActingId] = useState<number | null>(null)
  const [commentDraft, setCommentDraft] = useState<Record<number, string>>({})

  const load = async (pageArg = page) => {
    setIsLoading(true)
    try {
      const params: any = { page: pageArg }
      if (statusFilter) params.status = statusFilter
      const res = await leaveAPI.getAllRequests(params)
      const parsed = parsePaginatedResponse(res.data)
      setRequests(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load leave requests.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    setPage(1)
    load(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  const handleApprove = async (id: number) => {
    setActingId(id)
    try {
      await leaveAPI.approveRequest(String(id), commentDraft[id] || '')
      success('Leave request approved.')
      await load()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to approve request.'))
    } finally {
      setActingId(null)
    }
  }

  const handleReject = async (id: number) => {
    const comment = commentDraft[id]
    if (!comment) {
      toastError('Please add a comment explaining the rejection.')
      return
    }
    setActingId(id)
    try {
      await leaveAPI.rejectRequest(String(id), comment)
      success('Leave request rejected.')
      await load()
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to reject request.'))
    } finally {
      setActingId(null)
    }
  }

  const filterTabs = [
    { key: 'PENDING', label: 'Pending' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: '', label: 'All' },
  ]

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7">
        <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
          <Sparkles className="w-3 h-3" />
          Approvals
        </div>
        <h1
          className="text-3xl font-bold text-neutral-900 mb-1"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Leave Requests
        </h1>
        <p className="text-sm text-neutral-500">
          Review and action employee leave &amp; attendance correction requests
        </p>
      </div>

      {/* ── Filter tabs ── */}
      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 bg-white border border-neutral-200 rounded-xl p-1 shadow-sm">
          {filterTabs.map((tab) => {
            const active = statusFilter === tab.key
            return (
              <button
                key={tab.key || 'ALL'}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-[#F0821F] to-[#E4670F] text-white shadow-md shadow-[#E4670F]/25'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Result count chip */}
        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2 shadow-sm ml-auto">
          <Filter className="w-3.5 h-3.5 text-[#C24A0A]" />
          <span className="text-xs font-semibold text-neutral-800 tabular-nums">{count}</span>
          <span className="text-xs text-neutral-500">
            {count === 1 ? 'request' : 'requests'}
          </span>
        </div>
      </div>

      {/* ── Requests list ── */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
            <p className="text-sm text-neutral-500">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center">
              <Inbox className="w-8 h-8 text-neutral-400" />
            </div>
            <p className="text-sm font-medium text-neutral-700">No leave requests found</p>
            <p className="text-xs text-neutral-400">
              {statusFilter ? `There are no ${STATUS_LABELS[statusFilter]?.toLowerCase() || ''} requests right now.` : 'Nothing here yet.'}
            </p>
          </div>
        ) : (
          requests.map((req) => {
            const status = req.status || 'PENDING'
            const isPending = status === 'PENDING'
            const isActing = actingId === req.id
            const typeLabel = req.type === 'CORRECTION' ? 'Attendance Correction' : 'Leave'

            return (
              <div
                key={req.id}
                className="group bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden hover:shadow-lg hover:shadow-neutral-200/50 hover:ring-neutral-300/70 transition-all duration-300"
              >
                {/* Status accent bar */}
                <div className={`h-1 w-full ${status === 'APPROVED' ? 'bg-emerald-500' : status === 'REJECTED' ? 'bg-red-500' : 'bg-amber-500'}`} />

                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                    {/* Left — request details */}
                    <div className="flex-1 min-w-0">
                      {/* Top row: status pill + type */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLES[status] || 'bg-neutral-100 text-neutral-700 ring-neutral-200'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[status] || 'bg-neutral-400'}`} />
                          {STATUS_LABELS[status] || status}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-600 text-[11px] font-medium">
                          <FileText className="w-3 h-3" />
                          {typeLabel}
                        </span>
                      </div>

                      {/* Employee + meta */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-sm font-bold text-white ring-2 ring-white shadow-sm flex-shrink-0">
                          {(req.employee_name || 'E').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-neutral-900 truncate">
                            {req.employee_name || `Employee #${req.employee}`}
                            {req.employee_id && (
                              <span className="text-neutral-400 font-normal"> · {req.employee_id}</span>
                            )}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-neutral-500 mt-0.5 flex-wrap">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {req.target_date}
                            </span>
                            {req.employee_center_code && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Center: {req.employee_center_code}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Reason */}
                      <div className="bg-neutral-50 rounded-xl px-3.5 py-2.5 border border-neutral-100">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-0.5">
                          Reason
                        </p>
                        <p className="text-sm text-neutral-700 leading-relaxed">{req.reason}</p>
                      </div>

                      {/* Admin comment (if any) */}
                      {req.admin_comment && (
                        <div className="mt-3 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-3.5 py-2.5">
                          <MessageSquare className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 mb-0.5">
                              Admin Comment
                            </p>
                            <p className="text-sm text-blue-800 leading-relaxed">{req.admin_comment}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right — actions (only for pending) */}
                    {isPending && (
                      <div className="w-full lg:w-72 shrink-0 lg:border-l lg:border-neutral-100 lg:pl-5">
                        <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">
                          <MessageSquare className="w-3 h-3" />
                          Comment
                          <span className="text-neutral-400 normal-case font-normal tracking-normal">(required to reject)</span>
                        </label>
                        <textarea
                          placeholder="Add a note for the employee..."
                          value={commentDraft[req.id] || ''}
                          onChange={(e) => setCommentDraft({ ...commentDraft, [req.id]: e.target.value })}
                          className="w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all resize-none"
                          rows={3}
                        />
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => handleApprove(req.id)}
                            disabled={isActing}
                            className="group/btn flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white rounded-xl text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 hover:-translate-y-0.5"
                          >
                            <Check className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(req.id)}
                            disabled={isActing}
                            className="group/btn flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white rounded-xl text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-red-500/20 hover:shadow-lg hover:shadow-red-500/30 hover:-translate-y-0.5"
                          >
                            <X className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />
    </div>
  )
}


// import { useState, useEffect } from 'react'
// import { Check, X, Clock } from 'lucide-react'
// import { leaveAPI } from '../../services/api'
// import { useToast, getErrorMessage } from '../../components/Toast'

// export default function AdminLeavePage() {
//   const { success, error: toastError } = useToast()
//   const [requests, setRequests] = useState<any[]>([])
//   const [isLoading, setIsLoading] = useState(true)
//   const [statusFilter, setStatusFilter] = useState('PENDING')
//   const [actingId, setActingId] = useState<number | null>(null)
//   const [commentDraft, setCommentDraft] = useState<Record<number, string>>({})

//   const load = async () => {
//     setIsLoading(true)
//     try {
//       const params: any = {}
//       if (statusFilter) params.status = statusFilter
//       const res = await leaveAPI.getAllRequests(params)
//       setRequests(res.data.results || res.data)
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to load leave requests.'))
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   useEffect(() => {
//     load()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [statusFilter])

//   const handleApprove = async (id: number) => {
//     setActingId(id)
//     try {
//       await leaveAPI.approveRequest(String(id), commentDraft[id] || '')
//       success('Leave request approved.')
//       await load()
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to approve request.'))
//     } finally {
//       setActingId(null)
//     }
//   }

//   const handleReject = async (id: number) => {
//     const comment = commentDraft[id]
//     if (!comment) {
//       toastError('Please add a comment explaining the rejection.')
//       return
//     }
//     setActingId(id)
//     try {
//       await leaveAPI.rejectRequest(String(id), comment)
//       success('Leave request rejected.')
//       await load()
//     } catch (err: any) {
//       toastError(getErrorMessage(err, 'Failed to reject request.'))
//     } finally {
//       setActingId(null)
//     }
//   }

//   const statusColor = (status: string) =>
//     status === 'APPROVED'
//       ? 'bg-green-100 text-green-700'
//       : status === 'REJECTED'
//       ? 'bg-red-100 text-red-700'
//       : 'bg-yellow-100 text-yellow-700'

//   return (
//     <div className="p-6 md:p-8">
//       <div className="mb-8">
//         <h1 className="text-3xl font-bold text-secondary mb-2">Leave Requests</h1>
//         <p className="text-neutral-600">Review and action employee leave &amp; attendance correction requests</p>
//       </div>

//       <div className="mb-6 flex gap-2">
//         {['PENDING', 'APPROVED', 'REJECTED', ''].map((s) => (
//           <button
//             key={s || 'ALL'}
//             onClick={() => setStatusFilter(s)}
//             className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
//               statusFilter === s ? 'bg-primary text-white border-primary' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
//             }`}
//           >
//             {s || 'All'}
//           </button>
//         ))}
//       </div>

//       <div className="space-y-4">
//         {isLoading ? (
//           <div className="text-center py-12 text-neutral-600">Loading requests...</div>
//         ) : requests.length === 0 ? (
//           <div className="text-center py-12">
//             <Clock className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
//             <p className="text-neutral-600">No leave requests found.</p>
//           </div>
//         ) : (
//           requests.map((req) => (
//             <div key={req.id} className="bg-white rounded-lg shadow border border-neutral-200 p-6">
//               <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
//                 <div className="flex-1">
//                   <div className="flex items-center gap-3 mb-2 flex-wrap">
//                     <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor(req.status)}`}>
//                       {req.status}
//                     </span>
//                     <span className="text-xs text-neutral-500">{req.type === 'CORRECTION' ? 'Attendance Correction' : 'Leave'}</span>
//                   </div>
//                   <p className="font-semibold text-neutral-900">
//                     {req.employee_name || `Employee #${req.employee}`}
//                     {req.employee_id && <span className="text-neutral-500 font-normal"> ({req.employee_id})</span>}
//                   </p>
//                   {req.employee_center_code && (
//                     <p className="text-xs text-neutral-500">Center: {req.employee_center_code}</p>
//                   )}
//                   <p className="text-sm text-neutral-600">Date: {req.target_date}</p>
//                   <p className="text-neutral-700 mt-2">{req.reason}</p>
//                   {req.admin_comment && (
//                     <p className="text-sm text-neutral-500 mt-2 italic">Admin comment: {req.admin_comment}</p>
//                   )}
//                 </div>
//                 {req.status === 'PENDING' && (
//                   <div className="flex flex-col gap-2 w-full md:w-72 shrink-0">
//                     <textarea
//                       placeholder="Comment (required to reject)"
//                       value={commentDraft[req.id] || ''}
//                       onChange={(e) => setCommentDraft({ ...commentDraft, [req.id]: e.target.value })}
//                       className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary"
//                       rows={2}
//                     />
//                     <div className="flex gap-2">
//                       <button
//                         onClick={() => handleApprove(req.id)}
//                         disabled={actingId === req.id}
//                         className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
//                       >
//                         <Check className="w-4 h-4" /> Approve
//                       </button>
//                       <button
//                         onClick={() => handleReject(req.id)}
//                         disabled={actingId === req.id}
//                         className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
//                       >
//                         <X className="w-4 h-4" /> Reject
//                       </button>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           ))
//         )}
//       </div>
//     </div>
//   )
// }
